import React, { useEffect, useState } from 'react';
import { formatLiveMatchClock, shouldTickLiveClock } from '../utils/liveMatchClock';

/**
 * Live match status pill — half time, elapsed minute, stoppage time.
 * Re-anchors to each polled/websocket liveMinute (AppSync does not push minute
 * ticks); ticks forward during open play. In stoppage, holds the official
 * minute/extra until the next update so disconnects cannot invent 90+9 vs 90+5.
 * Period starts are only a fallback when no official minute has arrived yet.
 * @param {{ game: Record<string, unknown> }} props
 */
export default function LiveMatchBadge({ game }) {
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [minuteAnchor, setMinuteAnchor] = useState(null);
  const tick = shouldTickLiveClock(game);
  const liveMinute = game?.liveMinute ?? game?.lm;
  const liveExtraMinute = game?.liveExtraMinute ?? game?.lx;
  const livePhase = game?.livePhase ?? game?.lph;
  const status = game?.status;

  useEffect(() => {
    if (status !== 'live') {
      setMinuteAnchor(null);
      return;
    }
    const minute = Number(liveMinute);
    if (!Number.isFinite(minute)) {
      setMinuteAnchor(null);
      return;
    }
    const extra = Number(liveExtraMinute);
    setMinuteAnchor({
      observedAtMs: Date.now(),
      liveMinute: minute,
      liveExtraMinute: Number.isFinite(extra) && extra > 0 ? extra : 0
    });
  }, [status, liveMinute, liveExtraMinute, livePhase]);

  useEffect(() => {
    if (!tick) return undefined;
    setNowMs(Date.now());
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [tick, game?.firstHalfStartedAt, game?.secondHalfStartedAt, livePhase, status, liveMinute, liveExtraMinute]);

  const label = formatLiveMatchClock(game, nowMs, minuteAnchor);
  if (!label) return null;

  return (
    <span className="game-status game-status--live" aria-live="polite">
      <span className="game-status__live-dot" aria-hidden="true" />
      {label}
    </span>
  );
}
